/* eslint-disable @typescript-eslint/no-explicit-any */
import axios from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import { api } from 'services/api';
import { notificationContainer } from 'utils/notificationContainer';
import { PayloadAction } from '@reduxjs/toolkit';

import {
	assignUserRolesFailure,
	assignUserRolesRequest,
	assignUserRolesSuccess,
	getUserRolesFailure,
	getUserRolesGroupedFailure,
	getUserRolesGroupedRequest,
	getUserRolesGroupedSuccess,
	getUserRolesRequest,
	getUserRolesSuccess,
	getUserRolesByUserIdFailure,
	getUserRolesByUserIdRequest,
	getUserRolesByUserIdSuccess,
	updateUserRoleFailure,
	updateUserRoleRequest,
	updateUserRoleSuccess,
	removeUserRoleFailure,
	removeUserRoleRequest,
	removeUserRoleSuccess,
	getRolesRequest,
	getRolesSuccess,
	getRolesFailure,
} from './reducer';
import {
	TAssignUserRolesRequest,
	TGetUserRolesGroupedRequest,
	TGetUserRolesGroupedResponsePayload,
	TGetUserRolesByUserIdRequest,
	TGetUserRolesByUserIdResponsePayload,
	TGetUserRolesRequest,
	TGetUserRolesResponsePayload,
	TGetRolesResponsePayload,
	TRemoveUserRoleRequest,
	TUpdateUserRoleRequest,
} from './types';
import { ESortDirection } from '../../../ui/EasTable/types';

function* getRolesRequestWorker() {
	try {
		const response: TGetRolesResponsePayload = yield call(api.userRoles.getRoles);
		yield put(getRolesSuccess(response));
	} catch (error) {
		yield put(getRolesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getUserRolesGroupedRequestWorker({
	payload,
}: PayloadAction<TGetUserRolesGroupedRequest>) {
	try {
		console.log('getUserRolesGroupedRequestWorker payload', payload);
		const response: TGetUserRolesGroupedResponsePayload = yield call(
			api.userRoles.getGrouped,
			payload,
		);
		yield put(getUserRolesGroupedSuccess(response));
	} catch (error) {
		yield put(getUserRolesGroupedFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getUserRolesRequestWorker({ payload }: PayloadAction<TGetUserRolesRequest>) {
	try {
		const response: TGetUserRolesResponsePayload = yield call(api.userRoles.getUserRoles, payload);
		yield put(getUserRolesSuccess(response));
	} catch (error) {
		yield put(getUserRolesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* getUserRolesByUserIdRequestWorker({
	payload,
}: PayloadAction<TGetUserRolesByUserIdRequest>) {
	try {
		const response: TGetUserRolesByUserIdResponsePayload = yield call(
			api.userRoles.getUserRolesByUserId,
			payload,
		);
		yield put(getUserRolesByUserIdSuccess(response));
	} catch (error) {
		yield put(getUserRolesByUserIdFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* assignUserRolesRequestWorker({ payload }: PayloadAction<TAssignUserRolesRequest>) {
	try {
		yield call(api.userRoles.assignRoles, payload);
		yield put(assignUserRolesSuccess());
		notificationContainer('Roles assigned successfully', 'success');
		yield put(
			getUserRolesGroupedRequest({
				pageSize: 100,
				pageNumber: 1,
				orderBy: [{ fieldName: 'fullName', direction: ESortDirection.ASC }],
			}),
		);
		yield put(getUserRolesByUserIdRequest({ userId: payload.userId }));
	} catch (error) {
		yield put(assignUserRolesFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* updateUserRoleRequestWorker({ payload }: PayloadAction<TUpdateUserRoleRequest>) {
	try {
		yield call(api.userRoles.updateUserRole, payload);
		yield put(updateUserRoleSuccess());
		notificationContainer('Role updated successfully', 'success');
	} catch (error) {
		yield put(updateUserRoleFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

function* removeUserRoleRequestWorker({ payload }: PayloadAction<TRemoveUserRoleRequest>) {
	try {
		yield call(api.userRoles.removeUserRole, payload);
		yield put(removeUserRoleSuccess());
		yield put(
			getUserRolesGroupedRequest({
				pageSize: 100,
				pageNumber: 1,
				orderBy: [{ fieldName: 'fullName', direction: ESortDirection.ASC }],
			}),
		);
		yield put(getUserRolesByUserIdRequest({ userId: payload.userId }));
		notificationContainer('Role removed successfully', 'success');
	} catch (error) {
		yield put(removeUserRoleFailure());
		if (axios.isAxiosError(error)) {
			notificationContainer(String(error.response?.data?.message ?? 'Error'), 'error');
		}
	}
}

export function* userRolesSaga() {
	yield takeEvery(getRolesRequest.type, getRolesRequestWorker);
	yield takeEvery(getUserRolesGroupedRequest.type, getUserRolesGroupedRequestWorker);
	yield takeEvery(getUserRolesRequest.type, getUserRolesRequestWorker);
	yield takeEvery(getUserRolesByUserIdRequest.type, getUserRolesByUserIdRequestWorker);
	yield takeEvery(assignUserRolesRequest.type, assignUserRolesRequestWorker);
	yield takeEvery(updateUserRoleRequest.type, updateUserRoleRequestWorker);
	yield takeEvery(removeUserRoleRequest.type, removeUserRoleRequestWorker);
}
